import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import { defineStore } from "pinia"
import semver from 'semver'
import { reactive, ref, watch } from "vue"
import store from "@/store"
import { useWsStore } from '@/store/modules/wsStore'
import { getPrefixByUrl } from "@/utils"
import { resolveApiVersionByModulePrefix, resolveIsGrayVersion } from "@/utils/businessContext/versionContext"
import { getAppVersionCookie } from "@/utils/cache/cookies"
import { getLastSystemVersion, getNewestVersion, getSidebarStatus, getUpdateNoticeNeedShow, setLastSystemVersion, setSidebarStatus, setUpdateNoticeNeedShow } from "@/utils/cache/localStorage"
import { isFrontendArtifactReloadSupported } from "@/utils/frontendArtifactRouting"
import { getVersion } from "@/utils/packageInfo"
import { injectServiceDeps } from "@/utils/service"
/* eslint no-unused-vars: "off" -- 禁用未使用变量检查 */
export enum DeviceType {
  Mobile,
  Desktop,
}

interface ISidebar {
  opened: boolean
  withoutAnimation: boolean
  // 添加完全隐藏侧边栏的状态
  hidden: boolean
}

export const useAppStore = defineStore("app", () => {
  const sidebar: ISidebar = reactive({
    opened: getSidebarStatus() !== "closed",
    withoutAnimation: false,
    hidden: false,
  })
  const showUpdateNoticeDialog = ref(false)
  const showUpdateNoticeDialogMode = ref<'before' | 'after'>('before')
  const currentFrontVersion = ref(semver.coerce(getVersion() ?? '0.0.0') as semver.SemVer)
  const updateNoticeContent = ref('')
  const showTopNotice = ref(false)
  const showUpdateNotice = ref(false)
  const currentSystemVersion = ref<SystemVersionDTO | null>(null)

  // 监听 currentSystemVersion 变化，自动重建 WebSocket
  const wsStore = useWsStore()
  watch(
    currentSystemVersion,
    (newVal, oldVal) => {
      if (
        !oldVal ||
        newVal?.systemVersion?.systemVersion !== oldVal?.systemVersion?.systemVersion ||
        newVal?.systemVersion?.isGray !== oldVal?.systemVersion?.isGray
      ) {
        wsStore.resetWebSocket()
      }
    },
    { deep: true },
  )

  function getApiVersion(url: string, systemVersion = currentSystemVersion.value) {
    const modulePrefix = getPrefixByUrl(url)
    return resolveApiVersionByModulePrefix(
      modulePrefix,
      systemVersion,
    )
  }

  function getIsGray(systemVersion = currentSystemVersion.value) {
    return resolveIsGrayVersion(systemVersion)
  }

  injectServiceDeps({
    getApiVersion,
    getIsGray,
  })

  /**
   * 在路由钩子中，处理系统版本更新的通用方法
   * 路由钩子中，需要更新时，判断只要涉及到前端版本模块版本变动，直接完成更新，再补充后续的日志弹窗
   * 无论何种类型的更新，必定在此处完成更新（但更新日志可能需要补充弹窗）
   */
  function keepUpdateToMyVersion(options: { deferFrontendReload?: boolean } = {}) {
    const lastSystemVersion = getLastSystemVersion()
    const newestVersion = getNewestVersion()
    if (!newestVersion) return false
    // && rel.systemVersionId === lastSystemVersion.systemVersion?.systemVersionId
    const lastFrontVersion = lastSystemVersion?.versionModuleRels?.find(rel => rel.moduleType === 4)?.moduleVersion ?? 'v0.0.0'
    // 特殊情况，用户被灰度到，但是删掉cookie，却保留了localStorage中的last_system_version，然后访问地址没有填写路径，导致请求的html是稳定版，但此处isNewVersion判断是false，不会走更新
    const isNewVersion = lastSystemVersion?.systemVersion?.systemVersion !== newestVersion?.systemVersion?.systemVersion || getAppVersionCookie() !== lastFrontVersion
    // 无论是否新版本都要写入，因为可能有isGray和isLatest等属性变动
    setCurrentSystemVersion(newestVersion)
    // 仅在有新版本时处理
    if (isNewVersion) {
      // && rel.systemVersionId === newestVersion.systemVersion?.systemVersionId
      const newFrontVersion = newestVersion?.versionModuleRels?.find(rel => rel.moduleType === 4)?.moduleVersion ?? 'v0.0.0'
      const frontModuleNeedUpdate = semver.neq(currentFrontVersion.value, newFrontVersion)
      if (
        options.deferFrontendReload
        && newestVersion.systemVersion?.noticeContent
      ) {
        setUpdateNoticeNeedShow(newestVersion)
      }
      // 无论是否新版本都要写入，因为可能有isGray和isLatest等属性变动
      if (frontModuleNeedUpdate) {
        // 本地 Vite preview 不具备 CDN/Nginx 的版本路由能力。刷新只会
        // 重新加载同一份 dist，不能切换到 app_version 指向的制品。
        if (!isFrontendArtifactReloadSupported()) {
          return false
        }
        // reload导致此次更新日志丢失，需补上
        if (newestVersion.systemVersion?.noticeContent) {
          setUpdateNoticeNeedShow(newestVersion)
        }
        if (options.deferFrontendReload) {
          return true
        }
        window.location.reload()
      } else {
        if (options.deferFrontendReload) {
          return false
        }
        // TODO 是否需要该timeout
        setTimeout(() => {
          if (newestVersion.systemVersion?.noticeContent) {
            // 这里是更新完成通知
            setShowUpdateNoticeDialog(true, 'after')
          }
        }, 500)
      }
    } else {
      if (options.deferFrontendReload) {
        return false
      }
      // 补充遗漏的更新日志通知
      const updateNoticeNeedShow = getUpdateNoticeNeedShow()
      if (updateNoticeNeedShow && updateNoticeNeedShow.systemVersion?.noticeContent) {
        setShowUpdateNoticeDialog(true, 'after')
      }
    }
    return false
  }

  /**
   * ws消息通知后，处理系统版本更新的通用方法
   * 在ws消息通知中，先弹出消息，再处理更新逻辑
   * 强制更新，弹窗，用户点击我知道了，则关闭弹窗，并且刷新页面
   */
  function handleUpdateMessage() {
    const lastSystemVersion = getLastSystemVersion()
    const newestVersion = getNewestVersion()
    if (!newestVersion) return

    const isNewVersion = lastSystemVersion?.systemVersion?.systemVersion !== newestVersion?.systemVersion?.systemVersion

    // 无论是否新版本都要写入，因为可能有isGray和isLatest等属性变动
    // setCurrentSystemVersion(newestVersion)
    // 仅在有新版本时处理
    if (isNewVersion) {
      const newFrontVersion = newestVersion?.versionModuleRels?.find(rel => rel.moduleType === 4)?.moduleVersion ?? 'v0.0.0'
      const frontModuleNeedUpdate = semver.neq(currentFrontVersion.value, newFrontVersion)
      // 如果前端不需要更新，则直接静默完成更新，展示更新完成通知
      if (!frontModuleNeedUpdate) {
        setCurrentSystemVersion(newestVersion)
        // 更新完成通知
        if (newestVersion.systemVersion?.noticeContent) {
          setShowUpdateNoticeDialog(true, 'after')
        }
      } else {
        // 通过弹窗内部处理
        if (newestVersion.systemVersion?.noticeContent) {
          setShowUpdateNoticeDialog(true)
        }
      }
    }
  }

  function setCurrentSystemVersion(version: SystemVersionDTO | null) {
    setLastSystemVersion(version ?? undefined)
    currentSystemVersion.value = version
  }

  function setShowTopNotice(value: boolean) {
    showTopNotice.value = value
  }

  const setShowUpdateNoticeDialog = (value: boolean, mode: 'before' | 'after' = 'before') => {
    showUpdateNoticeDialog.value = value
    showUpdateNoticeDialogMode.value = mode
  }

  const getShowUpdateNoticeDialog = () => {
    return showUpdateNoticeDialog
  }

  const getUpdateNoticeContent = () => {
    return updateNoticeContent.value
  }

  const setUpdateNoticeContent = (content: string) => {
    updateNoticeContent.value = content
  }

  const device = ref<DeviceType>(DeviceType.Desktop)
  const fullscreenLoading = ref(false)

  const toggleSidebar = (withoutAnimation: boolean) => {
    sidebar.opened = !sidebar.opened
    sidebar.withoutAnimation = withoutAnimation
    if (sidebar.opened) {
      setSidebarStatus("opened")
    } else {
      setSidebarStatus("closed")
    }
  }
  const closeSidebar = (withoutAnimation: boolean) => {
    sidebar.opened = false
    sidebar.withoutAnimation = withoutAnimation
    setSidebarStatus("closed")
  }
  const openSidebar = (withoutAnimation: boolean) => {
    sidebar.opened = true
    sidebar.withoutAnimation = withoutAnimation
    setSidebarStatus("opened")
  }

  // 完全隐藏侧边栏的方法
  const hideSidebar = (withoutAnimation: boolean = false) => {
    sidebar.hidden = true
    sidebar.withoutAnimation = withoutAnimation
  }

  // 显示侧边栏的方法
  const showSidebar = (withoutAnimation: boolean = false) => {
    sidebar.hidden = false
    sidebar.withoutAnimation = withoutAnimation
  }

  const toggleDevice = (value: DeviceType) => {
    device.value = value
  }

  const setFullscreenLoading = (value: boolean) => {
    fullscreenLoading.value = value
  }

  const getFullscreenLoading = () => {
    return fullscreenLoading
  }

  return { device, sidebar, toggleSidebar, closeSidebar, openSidebar, toggleDevice, setFullscreenLoading, getFullscreenLoading, showUpdateNoticeDialog, showUpdateNoticeDialogMode, setShowUpdateNoticeDialog, getShowUpdateNoticeDialog, getUpdateNoticeContent, setUpdateNoticeContent, showTopNotice, showUpdateNotice, currentFrontVersion, currentSystemVersion, setCurrentSystemVersion, setShowTopNotice, keepUpdateToMyVersion, handleUpdateMessage, getIsGray, hideSidebar, showSidebar }
})

export function useAppStoreHook() {
  return useAppStore(store)
}
